
import {
    BsCodeSlash, BsTerminal, BsDiagram2, BsKanban, // IT
    BsGraphUp, BsCalculator, BsTable, BsPieChart, // Finance
    BsMegaphone, BsPalette, BsInstagram, BsBarChart, // Marketing
    BsPeople, BsFileEarmarkText, BsCalendarEvent, BsBuilding, // HR
    BsPen, BsCamera, BsMusicNote, BsBrush, BsEasel, BsLayoutTextSidebar, // Creative
    BsTools, BsBricks, BsCompass, BsCone, // Engineering
    BsHeartPulse, BsPrescription, BsActivity, BsClipboardData, // Healthcare
    BsBook, BsMortarboard, BsJournalBookmark, BsLightbulb, // Education
    BsHammer, BsFileEarmarkPdf, BsShieldCheck, BsArchive, // Legal
    BsMinecart, BsTruck, BsBoxSeam, BsMap, // Logistics/Manufacturing
    BsCpu, BsExclamationTriangle, BsBug, BsBezier // Science
} from 'react-icons/bs';

export interface Tool {
    id: string;
    name: string;
    icon: any;
    description: string;
    componentType: 'editor' | 'spreadsheet' | 'kanban' | 'whiteboard' | 'terminal' | 'calendar' | 'media' | 'analytics' | 'viewer' | 'overview' | 'milestones' | 'payments' | 'contract';
}

export const DEPARTMENTS: Record<string, string> = {
    'IT': 'Technology & Development',
    'FIN': 'Finance & Accounting',
    'MKT': 'Marketing & Content',
    'HR': 'Human Resources',
    'DES': 'Design & Creative',
    'ENG': 'Engineering & Architecture',
    'MED': 'Healthcare & Medical',
    'EDU': 'Education & Training',
    'LEG': 'Legal & Compliance',
    'SCI': 'Science & Research',
    'OPS': 'Operations & Logistics',
    'SAL': 'Sales & CRM',
    'RES': 'Real Estate & Constr.',
    'GEN': 'General / Other'
};

export const TOOL_REGISTRY: Record<string, Tool[]> = {
    'IT': [
        { id: 'code', name: 'IDE', icon: BsCodeSlash, description: 'Integrated Development Env', componentType: 'editor' },
        { id: 'terminal', name: 'Terminal', icon: BsTerminal, description: 'Command Line', componentType: 'terminal' },
        { id: 'diagrams', name: 'Architecture', icon: BsDiagram2, description: 'System Diagrams', componentType: 'whiteboard' },
        { id: 'kanban', name: 'Sprint Board', icon: BsKanban, description: 'Agile Task Tracking', componentType: 'kanban' }
    ],
    'FIN': [
        { id: 'ledger', name: 'General Ledger', icon: BsTable, description: 'Financial Records', componentType: 'spreadsheet' },
        { id: 'analytics', name: 'Analytics', icon: BsGraphUp, description: 'Financial Modeling', componentType: 'analytics' },
        { id: 'calc', name: 'Calculator', icon: BsCalculator, description: 'Scientific Calc', componentType: 'viewer' },
        { id: 'compliance', name: 'Compliance', icon: BsShieldCheck, description: 'Audit Logs', componentType: 'editor' }
    ],
    'MKT': [
        { id: 'campaigns', name: 'Campaigns', icon: BsKanban, description: 'Ad Tracking Board', componentType: 'kanban' },
        { id: 'design', name: 'Studio', icon: BsPalette, description: 'Asset Designer', componentType: 'whiteboard' },
        { id: 'social', name: 'Social Cal', icon: BsInstagram, description: 'Post Scheduling', componentType: 'calendar' },
        { id: 'copy', name: 'Copywriter', icon: BsPen, description: 'Content Editor', componentType: 'editor' }
    ],
    'DES': [
        { id: 'canvas', name: 'Canvas', icon: BsBrush, description: 'Digital Painting', componentType: 'whiteboard' },
        { id: 'assets', name: 'Asset Mgmt', icon: BsBoxSeam, description: 'File Manager', componentType: 'viewer' },
        { id: 'storyboard', name: 'Storyboard', icon: BsLayoutTextSidebar, description: 'Visual Planning', componentType: 'kanban' },
        { id: 'notes', name: 'Brainstorm', icon: BsLightbulb, description: 'Idea Pad', componentType: 'editor' }
    ],
    'ENG': [
        { id: 'cad', name: 'Blueprint', icon: BsCompass, description: 'Schematic View', componentType: 'whiteboard' },
        { id: 'specs', name: 'Specs', icon: BsClipboardData, description: 'Technical Docs', componentType: 'spreadsheet' },
        { id: 'tasks', name: 'Site Tasks', icon: BsCone, description: 'Project Mgmt', componentType: 'kanban' },
        { id: 'calc', name: 'Physics Calc', icon: BsCalculator, description: 'Load Calculator', componentType: 'viewer' }
    ],
    'MED': [
        { id: 'records', name: 'Patient Rec', icon: BsClipboardData, description: 'EMR System', componentType: 'spreadsheet' },
        { id: 'imaging', name: 'Imaging', icon: BsActivity, description: 'X-Ray/MRI Viewer', componentType: 'whiteboard' },
        { id: 'schedule', name: 'Rounds', icon: BsCalendarEvent, description: 'Shift Schedule', componentType: 'calendar' },
        { id: 'notes', name: 'Diagnosis', icon: BsPrescription, description: 'Clinical Notes', componentType: 'editor' }
    ],
    'EDU': [
        { id: 'gradebook', name: 'Gradebook', icon: BsTable, description: 'Student Grades', componentType: 'spreadsheet' },
        { id: 'whiteboard', name: 'Whiteboard', icon: BsEasel, description: 'Virtual Class', componentType: 'whiteboard' },
        { id: 'curriculum', name: 'Curriculum', icon: BsBook, description: 'Course Plans', componentType: 'kanban' },
        { id: 'library', name: 'Library', icon: BsArchive, description: 'Resource DB', componentType: 'viewer' }
    ],
    'LEG': [
        { id: 'contracts', name: 'Contracts', icon: BsFileEarmarkPdf, description: 'Doc Review', componentType: 'editor' },
        { id: 'cases', name: 'Case Files', icon: BsArchive, description: 'Case DB', componentType: 'spreadsheet' },
        { id: 'schedule', name: 'Court Dates', icon: BsHammer, description: 'Docket Cal', componentType: 'calendar' },
        { id: 'tasks', name: 'Discovery', icon: BsShieldCheck, description: 'Evidence Track', componentType: 'kanban' }
    ],
    'SCI': [
        { id: 'lab', name: 'Lab Notes', icon: BsJournalBookmark, description: 'Experiment Log', componentType: 'editor' },
        { id: 'data', name: 'Data Sets', icon: BsTable, description: 'Observation Data', componentType: 'spreadsheet' },
        { id: 'model', name: 'Modeling', icon: BsCpu, description: 'Simulation', componentType: 'whiteboard' },
        { id: 'tasks', name: 'Protocol', icon: BsBezier, description: 'Steps', componentType: 'kanban' }
    ],
    'OPS': [
        { id: 'inventory', name: 'Inventory', icon: BsBoxSeam, description: 'Stock Levels', componentType: 'spreadsheet' },
        { id: 'logistics', name: 'Map', icon: BsMap, description: 'Route Plan', componentType: 'whiteboard' },
        { id: 'fleet', name: 'Fleet', icon: BsTruck, description: 'Vehicle Track', componentType: 'kanban' },
        { id: 'metrics', name: 'Efficiency', icon: BsGraphUp, description: 'KPIs', componentType: 'analytics' }
    ],
    'GEN': [
        { id: 'notes', name: 'Notes', icon: BsFileEarmarkText, description: 'General Notes', componentType: 'editor' },
        { id: 'tasks', name: 'Tasks', icon: BsKanban, description: 'Task List', componentType: 'kanban' },
        { id: 'whiteboard', name: 'Whiteboard', icon: BsDiagram2, description: 'Sketchpad', componentType: 'whiteboard' },
        { id: 'calendar', name: 'Calendar', icon: BsCalendarEvent, description: 'Schedule', componentType: 'calendar' }
    ]
};
